import React, { useCallback, useContext, useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useFilestack } from '@shared/utils/filestack';
import { useTranslation } from '@shared/core';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { AnalyticsContext } from '@shared/core/analytics/context';
import {
  UploadPhotoFromUrlPayload,
  useAddMediaToCollectionMutation,
  useCreateMediaPhotoMutation,
  useUploadPhotosFromUrlsMutation,
  useUploadMediaFromUrlsMutation,
} from '@graphql/generated';
import { addAction } from '@shared/utils/logger';
import { evictMediaCollectionResponse, isValidPhotoAlbumMultiPhotoUploadPayload, PHOTO_UPLOAD_TELEMETRY_CATEGORY } from '@shared/utils/photoUpload';
import type { PhotoUploadUseCase } from '@shared/utils/photoUpload';
import { usePhotoUploadPolling } from '@shared/hooks/usePhotoUploadPolling';
import { PickerFileMetadata } from 'filestack-js';

export type UsePhotoUploadProps = Readonly<{
  eventId: string;
  collectionId?: string;
  containerId: string;
  updateUploadStatus?: (isUploading: boolean, inProgressCount?: number) => void;
  showPhotoLibrary?: boolean;
  onOpenPickerDialog: () => void;
  refetchQueries: string[];
  useCase: PhotoUploadUseCase;
}>;

export type UsePhotoUploadReturn = Readonly<{
  handleAddPhotosCTAClick: () => void;
  handleOpenFilePicker: () => void;
  fileUploadStatusData: {
    isUploading: boolean;
    imageCount: number;
  };
}>;

export const usePhotoUpload = (props: UsePhotoUploadProps): UsePhotoUploadReturn => {
  const { eventId, collectionId, containerId, updateUploadStatus, showPhotoLibrary = true, onOpenPickerDialog, refetchQueries, useCase } = props;

  const [fileUploadStatusData, setFileUploadStatusData] = useState<{
    isUploading: boolean;
    imageCount: number;
  }>({ isUploading: false, imageCount: 0 });

  const { value: photoAlbumMultiPhotoUploadV2Value, payload: photoAlbumMultiPhotoUploadV2Payload } = useFeatureValue('photoAlbumMultiPhotoUploadEnabledV2');
  const photoAlbumMultiPhotoUploadV2Enabled = photoAlbumMultiPhotoUploadV2Value === 'on';
  const [isAllCollectionPhotosEnabled] = useIsFeatureEnabled('allCollectionPhotosEnabled');

  const maxFiles = photoAlbumMultiPhotoUploadV2Enabled
    ? isValidPhotoAlbumMultiPhotoUploadPayload(photoAlbumMultiPhotoUploadV2Payload)
      ? photoAlbumMultiPhotoUploadV2Payload.maxFiles
      : 5
    : 5;

  const { track } = useContext(AnalyticsContext);
  const { tq } = useTranslation('photoUploader');
  const { toast } = useToast();
  const { eventHandle } = useEventInfo();

  const [uploadPhotosFromUrls] = useUploadPhotosFromUrlsMutation();
  const [createMediaPhoto] = useCreateMediaPhotoMutation();
  const [addMediaToCollection] = useAddMediaToCollectionMutation();
  const [uploadMediaFromUrls] = useUploadMediaFromUrlsMutation();

  const photoUploadStatusUpdate = useCallback(
    (isUploading: boolean, imageCount?: number) => {
      setFileUploadStatusData(prev => ({
        isUploading,
        imageCount: imageCount !== undefined ? imageCount : prev.imageCount,
      }));
      updateUploadStatus?.(isUploading, imageCount);
    },
    [updateUploadStatus],
  );

  const { startPolling } = usePhotoUploadPolling({
    eventId,
    collectionId,
    eventHandle,
    updateUploadStatus: photoUploadStatusUpdate,
    refetchQueries,
  });

  const fileUploadJob = useEventCallback(async (photoId: string) => {
    const { data } = await createMediaPhoto({
      variables: {
        payload: {
          eventId,
          photoId,
        },
      },
      ...(!collectionId && { refetchQueries: ['GetEventMedia'], awaitRefetchQueries: true }),
      update: cache => {
        if (eventHandle) {
          evictMediaCollectionResponse({ cache, eventHandle: eventHandle });
        }
      },
    });
    const mediaId = data?.createMediaPhoto?.id;
    if (mediaId) {
      if (collectionId) {
        track({
          category: PHOTO_UPLOAD_TELEMETRY_CATEGORY,
          action: 'uploadAlbumPhoto',
          actionType: 'click',
          extraInfo: {
            eventId,
            collectionId,
            mediaId,
            photoId,
            photoUrl: data.createMediaPhoto?.photo.url || '',
          },
          context: { useCase },
        });
        await addMediaToCollection({
          variables: {
            payload: {
              collectionId,
              mediaId,
            },
          },
          refetchQueries: ['MediaCollections', 'MediaCollectionById', 'GetEventMedia'],
          awaitRefetchQueries: true,
        });
      }
      photoUploadStatusUpdate(false, 0);
    }
  });

  const showUploadErrorToast = useCallback(() => {
    toast(tq`An error occurred while saving your photo. Please try again.`, {
      icon: <WarningTriangle />,
    });
  }, [toast, tq]);

  const handleAsyncUpload = useCallback(
    async (filesUploaded: PickerFileMetadata[], previewUrls: string[]) => {
      photoUploadStatusUpdate(true, filesUploaded.length);

      const photos = filesUploaded.map((file, index) => ({
        assetId: file.filename,
        url: previewUrls[index],
      }));

      try {
        const { data, errors } = await uploadMediaFromUrls({
          variables: {
            payload: {
              eventId,
              collectionId,
              photos,
            },
          },
        });

        if (errors || !data?.uploadMediaFromUrls) {
          addAction('uploadMediaFromUrls', { status: 'failed', errors });
          showUploadErrorToast();
          photoUploadStatusUpdate(false, 0);
          return;
        }

        const { uploadId } = data.uploadMediaFromUrls;
        track({
          category: PHOTO_UPLOAD_TELEMETRY_CATEGORY,
          action: 'uploadAlbumPhoto',
          actionType: 'click',
          extraInfo: {
            eventId,
            collectionId: collectionId || 'allPhotos',
            uploadId,
          },
          context: { useCase },
        });

        startPolling(uploadId);
      } catch (e) {
        addAction('handleAsyncUpload', { status: 'failed', error: e });
        showUploadErrorToast();
        photoUploadStatusUpdate(false, 0);
      }
    },
    [photoUploadStatusUpdate, uploadMediaFromUrls, eventId, collectionId, track, useCase, startPolling, showUploadErrorToast],
  );

  const handleLegacyMultiUpload = useCallback(
    async (filesUploaded: PickerFileMetadata[], previewUrls: string[]) => {
      photoUploadStatusUpdate(true, filesUploaded.length);

      const payload: UploadPhotoFromUrlPayload[] = filesUploaded.map((file, index) => ({
        assetId: file.filename,
        containerId,
        url: previewUrls[index],
      }));

      try {
        const { data, errors } = await uploadPhotosFromUrls({ variables: { payload } });

        if (errors) {
          showUploadErrorToast();
          photoUploadStatusUpdate(false, 0);
          return;
        }

        const photoDataArray = data?.uploadPhotosFromUrls || [];
        await Promise.all(photoDataArray.map(photoData => photoData?.id && fileUploadJob(photoData.id)))
          .catch(() => {
            showUploadErrorToast();
          })
          .finally(() => {
            photoUploadStatusUpdate(false, 0);
          });
      } catch (e) {
        addAction('handleLegacyMultiUpload', { status: 'failed', error: e });
        showUploadErrorToast();
        photoUploadStatusUpdate(false, 0);
      }
    },
    [photoUploadStatusUpdate, containerId, uploadPhotosFromUrls, fileUploadJob, showUploadErrorToast],
  );

  const { open: openFilePicker, getPhotosPreviewUrls } = useFilestack({
    containerId,
    usageContext: `${useCase.area}.${useCase.subArea}`,
    accept: 'image/*',
    maxFiles,
    transformations: {
      crop: true,
      rotate: true,
    },
    onUploadDone: async (res): Promise<void> => {
      if (res.filesFailed.length) {
        showUploadErrorToast();
        openFilePicker();
        return;
      }

      const previewUrls = await getPhotosPreviewUrls({ urls: res.filesUploaded.map(f => f.url) });

      if (photoAlbumMultiPhotoUploadV2Enabled) {
        await handleAsyncUpload(res.filesUploaded, previewUrls);
      } else {
        await handleLegacyMultiUpload(res.filesUploaded, previewUrls);
      }
    },
  });

  const handleOpenFilePicker = useCallback(() => {
    track({
      category: PHOTO_UPLOAD_TELEMETRY_CATEGORY,
      action: 'openFilePicker',
      actionType: 'click',
      extraInfo: {
        eventId,
        collectionId: collectionId || 'allPhotos',
      },
      context: { useCase },
    });
    openFilePicker();
  }, [collectionId, eventId, openFilePicker, track, useCase]);

  const handleAddPhotosCTAClick = useCallback(() => {
    track({
      category: PHOTO_UPLOAD_TELEMETRY_CATEGORY,
      action: 'uploadAlbumPhotoCTAClicked',
      actionType: 'click',
      extraInfo: {
        eventId,
        collectionId: collectionId || 'allPhotos',
      },
      context: { useCase },
    });
    if (isAllCollectionPhotosEnabled && showPhotoLibrary) {
      onOpenPickerDialog();
    } else {
      handleOpenFilePicker();
    }
  }, [isAllCollectionPhotosEnabled, collectionId, eventId, handleOpenFilePicker, onOpenPickerDialog, showPhotoLibrary, track, useCase]);

  return {
    handleAddPhotosCTAClick,
    handleOpenFilePicker,
    fileUploadStatusData,
  };
};
